import {
  Box,
  Button,
  Chip,
  FormControl,
  Grid,
  IconButton,
  MenuItem,
  OutlinedInput,
  Select,
  TextField,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import dayjs from 'dayjs';
import { Form, withFormik } from 'formik';
import React, { memo, useCallback, useEffect, useRef, useState } from 'react';
import * as yup from 'yup';

import ClearIcon from 'common/shared-ui/src/icons/ClearIcon';

import { useTranslation } from '../../../../../packages/common/utils/getLocalString';

// Original class: dayOfItems
const StyledDayOfItems = styled(Box)(({ theme }) => ({
  '& .MuiPaper-root': {
    marginTop: theme.spacing(3),
  },

  '& .MuiList-root': {
    paddingBottom: 0,
    paddingTop: 0,
  },
}));
// Original class: calendarHeaderRoot
const StyledCalendarHeaderRoot = styled(Grid)(({ theme }) => ({
  padding: theme.spacing(5, 10),
  borderRadius: theme.spacing(4),
  backgroundColor: '#ffffff',
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(5),
    '& > div': {
      flexDirection: 'column',
    },
  },
}));
// Original class: workHoursField
const StyledWorkHoursField = styled(TextField)(({ theme }) => ({
  '& input': {
    ...theme.typography.h5,
  },
}));
// Original class: offDaysSelect
const StyledOffDaysSelect = styled(Select)(({ theme }) => ({
  padding: theme.spacing(1),
  width: '100%',
  maxWidth: 353,

  '& > div': {
    padding: 0,
    overflowY: 'hidden !important',
    overflowX: 'scroll !important',
    minHeight: '30px !important',
    '&::-webkit-scrollbar': {
      display: 'none',
    },
  },

  '& .MuiSelect-icon': {
    display: 'none',
  },
}));
// Original class: chosenOffDay
const StyledChosenOffDay = styled(Box)(({ theme }) => ({
  padding: theme.spacing(1, 2),
  backgroundColor: '#ffffff',
  border: '1px solid #E4E4EF',
  borderRadius: theme.spacing(4),
  marginRight: theme.spacing(1),
  maxHeight: 30,
  zIndex: 1400,
  ...theme.typography.h5,

  '& .MuiChip-label': {
    padding: 0,
  },
}));
// Original class: offDayChip
const StyledOffDayChip = styled(Chip)(theme => ({
  '&.MuiChip-root': {
    height: 'unset',
    backgroundColor: 'transparent',
    textTransform: 'capitalize',
  },
}));
// Original class: offDayRemove
const StyledOffDayRemove = styled(IconButton)(({ theme }) => ({
  '&.MuiButtonBase-root': {
    padding: 0,
    marginLeft: theme.spacing(1),
  },
}));
// Original class: menuItem
const StyledMenuItem = styled(MenuItem)(theme => ({
  '&.Mui-selected': {
    background: '#DCDFF4 !important',
  },
}));
const CalendarHeader = ({
  values,
  errors,
  setOffDays,
  setFieldValue,
  offDays,
}) => {
  const getLocalString = useTranslation();
  const isSmallScreen = useMediaQuery(theme =>
    theme.breakpoints.maxWidth('lg'),
  );
  const isSmallestScreen = useMediaQuery(theme =>
    theme.breakpoints.maxWidth('sm'),
  );

  const { current: dayOfWeeks } = useRef(
    new Array(7).fill(null).map((day, i) => {
      return {
        name: dayjs()
          .set('day', i + 1 || 0)
          .format('dddd'),
        dayOfWeek: i,
      };
    }),
  );

  const handleChangeWorkHours = useCallback(
    e => {
      const regex = /^([01]?[0-9]|2[0-4])$/;
      if (regex.test(e.currentTarget.value) || e.currentTarget.value === '') {
        setFieldValue('workHours', Number(e.currentTarget.value));
      }
    },
    [setFieldValue],
  );

  const handleRemoveOffDay = useCallback(
    e => {
      const filteredOffDays = offDays.filter(day => {
        return day !== Number(e.currentTarget.attributes.value.nodeValue);
      });
      return setOffDays(filteredOffDays);
    },
    [offDays, setOffDays],
  );

  const handleChange = event => {
    const {
      target: { value },
    } = event;
    setOffDays(typeof value === 'string' ? value.split(',') : value);
    setFieldValue('offDays', value);
  };

  const { current: menuProps } = useRef({
    day: 1,
  });

  return (
    <Grid container direction='column' wrap='nowrap'>
      <Grid
        container
        wrap={isSmallScreen ? 'wrap' : 'nowrap'}
        sx={{
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: isSmallScreen ? 2.5 : 7.5,
          marginTop: 2.5,
        }}
      >
        <Typography variant='h1'>
          {getLocalString('AdminCalendar', 'title')}
        </Typography>
        <Box
          sx={{
            marginTop: isSmallestScreen ? 7.5 : 0,
            marginLeft: isSmallScreen ? 'auto' : '0',
          }}
        >
          <Button type='submit' disableRipple variant='defaultGreyPrimary'>
            {getLocalString('buttons', 'save')}
          </Button>
        </Box>
      </Grid>
      <StyledCalendarHeaderRoot
        container
        direction={isSmallScreen ? 'column-reverse' : 'row'}
        sx={{
          marginBottom: 2.5,
        }}
      >
        <Grid
          sx={{
            display: 'flex',
            alignItems: 'center',
            flex: 1,
            width: '100%',
            marginTop: isSmallScreen ? 3 : 0,
          }}
        >
          <Typography
            variant='h5'
            sx={{
              marginRight: isSmallScreen ? 0 : 2.5,
              flex: isSmallScreen ? 0.5 : 0.3,
            }}
          >
            {getLocalString('AdminCalendar', 'labels', 'defaultHours')}
          </Typography>
          <Box
            sx={{
              marginRight: isSmallScreen ? 0 : 12,
              width: '100%',
              flex: isSmallScreen ? 0.5 : 0.7,
            }}
          >
            <StyledWorkHoursField
              onChange={handleChangeWorkHours}
              value={values.workHours}
              type='number'
              error={!!errors.workHours}
            />
          </Box>
        </Grid>
        <Grid
          sx={{
            display: 'flex',
            alignItems: 'center',
            flex: 1,
            width: '100%',
          }}
        >
          <Typography
            variant='h5'
            sx={{
              marginRight: isSmallScreen ? 0 : 25,
              whiteSpace: 'nowrap',
              flex: isSmallScreen ? 0.5 : 0.3,
            }}
          >
            {getLocalString('AdminCalendar', 'labels', 'daysOff')}
          </Typography>
          <Box
            sx={{
              marginRight: isSmallScreen ? 0 : 12,
              width: '100%',
              flex: isSmallScreen ? 0.5 : 0.7,
            }}
          >
            <StyledOffDaysSelect
              multiple
              value={values.offDays}
              onChange={handleChange}
              input={<OutlinedInput onClick={e => e.stopPropagation()} />}
              error={!!errors.offDays}
              renderValue={selected => (
                <Box
                  sx={{
                    display: 'flex',
                    flexWrap: 'nowrap',
                    alignItems: 'center',
                  }}
                >
                  {selected.map(value => {
                    return (
                      <StyledChosenOffDay
                        wrap='nowrap'
                        key={value}
                        sx={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                        }}
                      >
                        <StyledOffDayChip label={dayOfWeeks[value - 1]?.name} />
                        <StyledOffDayRemove
                          value={value}
                          disableRipple
                          onClick={handleRemoveOffDay}
                        >
                          <Box
                            sx={{
                              width: 16,
                              height: 16,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                            }}
                          >
                            <ClearIcon viewBox='0 0 24 24' color='primary' />
                          </Box>
                        </StyledOffDayRemove>
                      </StyledChosenOffDay>
                    );
                  })}
                </Box>
              )}
              MenuProps={{
                ...menuProps,
                slots: { list: StyledDayOfItems },
              }}
            >
              {dayOfWeeks.map((day, i, array) => {
                return (
                  <StyledMenuItem
                    key={day.dayOfWeek}
                    value={array[i].dayOfWeek + 1}
                  >
                    <Typography
                      variant='h5'
                      sx={{
                        textTransform: 'capitalize',
                      }}
                    >
                      {array[i].name}
                    </Typography>
                  </StyledMenuItem>
                );
              })}
            </StyledOffDaysSelect>
          </Box>
        </Grid>
      </StyledCalendarHeaderRoot>
    </Grid>
  );
};

export default memo(CalendarHeader);
